// src/pages/Login.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import { openExternal, isEmbedded as checkIfEmbedded } from '../utils/nexo';
import { getAuth } from 'firebase/auth';

function Login({ callback = false }) {
  const { currentUser, login, auth } = useAuth();
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Mostrar mensaje si la sesión expiró
  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      setSessionExpired(true);
      setError('Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.');
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  // Detectar si estamos en un iframe embebido usando la utilidad nexo
  useEffect(() => {
    const embedded = checkIfEmbedded() || window !== window.parent;
    setIsEmbedded(embedded);
    console.log('[Login] Estado de embebido detectado:', embedded);
  }, []);

  // Si el usuario ya está autenticado, redirigir al dashboard
  useEffect(() => {
    if (currentUser && !isProcessingCallback) {
      console.log('[Login] Usuario autenticado detectado, redirigiendo a dashboard');
      navigate('/');
    }
  }, [currentUser, navigate, isProcessingCallback]);

  // Procesar el callback de OAuth2
  useEffect(() => {
    const processCallback = async () => {
      console.log('[Login] useEffect disparado. callback:', callback, 'location.search:', location.search);
      
      // Diagnóstico detallado de la ubicación para solucionar problemas de redirección
      console.log('[Login] URL completa:', window.location.href);
      console.log('[Login] Pathname:', window.location.pathname);
      console.log('[Login] Referrer:', document.referrer);
      
      // Guardar diagnósticos en sessionStorage
      sessionStorage.setItem('loginCallbackUrl', window.location.href);
      sessionStorage.setItem('loginCallbackReferrer', document.referrer || 'no-referrer');
      sessionStorage.setItem('loginCallbackTime', new Date().toISOString());
      
      // Solo procesamos si estamos en un callback, no hemos procesado aún, y tenemos parámetros en la URL
      if (callback && !callbackProcessed && location.search) {
        setIsProcessingCallback(true);
        setError('');
        sessionStorage.setItem('loginCallbackProcessingStart', new Date().toISOString());
        
        try {
          const params = new URLSearchParams(location.search);
          const token = params.get('token');
          // Persistimos el custom token completo para que ApiTokenService pueda convertirlo a sesión backend
          if (token) {
            try {
              localStorage.setItem('firebase_custom_token', token);
            } catch (e) {
              console.warn('[Login] No se pudo persistir firebase_custom_token en localStorage:', e);
            }
          }
          const tiendaId = params.get('tiendaId');
          console.log('[Login] Parámetros recibidos:', Object.fromEntries(params.entries()));
          
          // Guardar tiendaId para referencia
          if (tiendaId) {
            sessionStorage.setItem('lastTiendaId', tiendaId);
          }

          if (!token) {
            console.error('[Login] Token ausente en callback URL. location.search:', location.search);
            setError('No se recibió un token en la URL. Por favor, intenta iniciar sesión nuevamente.');
            setCallbackProcessed(true);
            setIsProcessingCallback(false);
            return;
          }

          console.log('[Login] Token recibido, intentando login en Firebase...');
          
          try {
            // Guardar token en sessionStorage para debugging
            sessionStorage.setItem('lastAuthToken', token.substring(0, 10) + '...');
            sessionStorage.setItem('authTimestamp', new Date().toISOString());
            
            // Asegurarse de que no hay auth state anterior
            sessionStorage.removeItem('authSuccess');
            sessionStorage.removeItem('authError');
            
            // Intentar login - importante await para capturar errores
            const user = await login(token);
            console.log('[Login] Login exitoso con usuario:', user?.uid);
            
            // Comprobar inmediatamente si currentUser está establecido
            if (user) {
              console.log('[Login] Usuario autenticado correctamente, verificando sesión activa...');
              sessionStorage.setItem('loginSuccessTime', new Date().toISOString());
              
              // Espera un poco más larga para asegurar que Firebase Auth establece la sesión
              setTimeout(() => {
                console.log('[Login] Verificando sesión antes de redirección...');
                
                // Comprobar que Firebase Auth reconoce la sesión activa
                const firebaseAuth = getAuth();
                if (firebaseAuth.currentUser) {
                  console.log('[Login] Sesión activa confirmada, navegando a dashboard');
                  // Redirigir directamente al dashboard
                  window.location.replace('/');
                } else {
                  console.warn('[Login] Firebase no reconoce al usuario como autenticado aún, esperando...');
                  // Intentar una última vez después de una espera adicional
                  setTimeout(() => {
                    console.log('[Login] Última verificación de sesión, redirigiendo de todos modos');
                    window.location.replace('/');
                  }, 1000);
                }
              }, 800);
            }
          } catch (authErr) {
            console.error('[Login] Error específico de autenticación:', authErr);
            sessionStorage.setItem('loginAuthError', authErr.message || authErr.toString());
            sessionStorage.setItem('loginAuthErrorTime', new Date().toISOString());
            
            setError('Error de autenticación: ' + (authErr.message || authErr.toString()));
            
            // Si es un error recún recibido del backend, mostramos el mensaje pero no redirigimos
            // para que el usuario pueda intentar de nuevo
            if (authErr.message && authErr.message.includes('proporcionó un token')) {
              console.warn('[Login] Token inválido o ausente, permaneciendo en pantalla de login');
            } else {
              // Si es otro tipo de error, intentamos navegar al home después de mostrar el mensaje
              console.log('[Login] Intentando navegar al home después del error...');
              setTimeout(() => {
                window.location.replace('/');
              }, 3000);
            }
          }
        } catch (err) {
          console.error('[Login] Error general al procesar callback de OAuth:', err);
          console.error('[Login] search params:', location.search);
          
          // Guardar detalles del error para diagnóstico
          sessionStorage.setItem('loginGeneralError', err.message || err.toString());
          sessionStorage.setItem('loginGeneralErrorTime', new Date().toISOString());
          sessionStorage.setItem('loginSearchParams', location.search);
          
          setError('Error al procesar login: ' + (err.message || err.toString()));
          
          // Intentar una última validación después de un error
          setTimeout(() => {
            // Si a pesar del error el usuario está autenticado, redireccionar
            const firebaseAuth = getAuth();
            if (firebaseAuth.currentUser) {
              console.log('[Login] A pesar del error, se detectó usuario autenticado. Redirigiendo...');
              window.location.replace('/');
            }
          }, 1000);
        } finally {
          setIsProcessingCallback(false);
          setCallbackProcessed(true);
          sessionStorage.setItem('loginCallbackProcessingEnd', new Date().toISOString());
        }
      } else if (callback && !callbackProcessed && !location.search) {
        // Caso donde estamos en /auth/callback pero sin parámetros (posible error)
        console.warn('[Login] Callback sin parámetros detectado');
        sessionStorage.setItem('loginEmptyCallbackDetected', 'true');
        sessionStorage.setItem('loginEmptyCallbackTime', new Date().toISOString());
        
        setError('No se detectaron parámetros de autenticación. Por favor, intenta iniciar sesión nuevamente desde Tiendanube.');
        setCallbackProcessed(true);
        setIsProcessingCallback(false);
        
        // Verificar una última vez si hay sesión activa a pesar de la falta de parámetros
        setTimeout(() => {
          const firebaseAuth = getAuth();
          if (firebaseAuth.currentUser) {
            console.log('[Login] Se detectó una sesión activa a pesar de la falta de parámetros. Redirigiendo...');
            window.location.replace('/');
          }
        }, 800);
      }
    };
    processCallback();
  }, [callback, callbackProcessed, location.search, login, navigate]);
  
  // Efecto adicional para detectar un usuario ya autenticado en cualquier momento
  useEffect(() => {
    const checkAuthState = () => {
      const firebaseAuth = getAuth();
      const currentAuthUser = firebaseAuth.currentUser;
      if (currentAuthUser && !isProcessingCallback) {
        console.log('[Login] Usuario detectado en auth.currentUser:', currentAuthUser.uid);
        sessionStorage.setItem('authUserDetectedInLogin', currentAuthUser.uid);
        
        // Si detectamos un usuario y no estamos en medio del proceso de callback,
        // redirigir al dashboard
        if (!callback || callbackProcessed) {
          console.log('[Login] Redirigiendo al dashboard por usuario ya autenticado');
          window.location.replace('/');
        }
      }
    };
    
    // Verificar si hay un usuario ya autenticado al montar el componente
    checkAuthState();
    
    // Establecer un intervalo para verificar periódicamente si hay un usuario autenticado
    // Útil para casos donde el observer de Firebase no se dispara correctamente
    const interval = setInterval(checkAuthState, 2000);
    
    return () => clearInterval(interval);
  }, [navigate, isProcessingCallback, callback, callbackProcessed]);

  // Redirigir a la página de inicio de sesión de Tienda Nube
  const handleLogin = () => {
    // Limpiar cualquier dato de sesión anterior antes de iniciar el flujo
    sessionStorage.removeItem('lastAuthToken');
    sessionStorage.removeItem('authTimestamp');
    
    // URL de la API de Functions que inicia el flujo de OAuth2
    // Usar SIEMPRE el dominio personalizado en producción para evitar errores de redirect_uri y CSP
    const isLocal = window.location.hostname === 'localhost';
    const loginUrl = isLocal
      ? 'http://localhost:5001/nubelytics/us-central1/api/auth/login'
      : 'https://nubelytics.com/api/auth/login'; // Dominio final para OAuth y Tiendanube

    console.log('[Login] Iniciando autenticación con URL:', loginUrl);
    
    // Si estamos embebidos en un iframe, usar nexo.openExternal para cumplir CSP
    // o fallback a window.open si no estamos en admin Tiendanube
    if (isEmbedded) {
      console.log('[Login] Detectado en iframe, abriendo autenticación vía Nexo');
      openExternal(loginUrl);
    } else {
      // Guardar timestamp para debugging
      sessionStorage.setItem('loginAttemptTimestamp', new Date().toISOString());
      window.location.href = loginUrl;
    }
  };

  // Feedback visual y UX mejorado
  if (isProcessingCallback) {
    console.log('[Login] Mostrando pantalla de carga durante procesamiento de callback');
    return <LoadingScreen message="Procesando autenticación..." />;
  }
  
  // Estilo adaptado para embebido en iframe si es necesario
  const containerStyle = {
    maxWidth: 420, 
    margin: isEmbedded ? '10px auto' : '48px auto', 
    padding: isEmbedded ? 20 : 32, 
    background: '#fff', 
    borderRadius: 12, 
    boxShadow: '0 4px 24px rgba(52,152,219,0.08)', 
    textAlign: 'center'
  };

  return (
    <main
      aria-labelledby="login-title"
      style={containerStyle}
    >
      <h1 id="login-title" style={{ fontSize: '2rem', marginBottom: 12, color: '#2563eb', letterSpacing: '-1px' }}>
        Iniciar sesión en Nubelytics
      </h1>
      <p style={{ color: '#555', marginBottom: 18 }}>
        Conecta tu tienda Tiendanube para acceder a analíticas avanzadas.
      </p>
      {error && (
        <section
          aria-live="assertive"
          role="alert"
          style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: 16, marginBottom: 18 }}
        >
          <strong>Error:</strong> {error}
          <br />
          <button
            style={{ marginTop: 14, padding: '10px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            onClick={handleLogin}
            aria-label="Reintentar login"
          >
            Reintentar login
          </button>
        </section>
      )}
      <button
        style={{ marginTop: 30, padding: '14px 32px', fontSize: '1.15rem', background: 'linear-gradient(90deg,#2563eb,#3bc4f7)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, boxShadow: '0 2px 8px rgba(52,152,219,0.08)' }}
        onClick={handleLogin}
        aria-label="Iniciar sesión con Tiendanube"
      >
        Iniciar sesión con Tiendanube
      </button>
      <div style={{ marginTop: 30, fontSize: 13, color: '#888' }}>
        ¿Problemas para iniciar sesión? <a href="mailto:soporte@nubelytics.com" style={{ color: '#2563eb', textDecoration: 'underline' }}>Contactar soporte</a>
      </div>
      <div style={{ marginTop: 18, fontSize: 12, color: '#aaa' }}>
        Nubelytics &copy; {new Date().getFullYear()} — Todos los derechos reservados.
      </div>
    </main>
  );
}

export default Login;
